import * as React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { TextInputFormProps } from './types';
import { Border, Color } from '@/GlobalStyles';
import FTextInput from '@/components/Form/TextInput';
import { Text, useTheme } from 'react-native-paper';
import { useState } from 'react';
import { TextInput as NativeTextInput } from 'react-native/Libraries/Components/TextInput/TextInput';

type TextAreaProps = TextInputFormProps & {
  minHeight?: number;
  maxHeight?: number;
  accessoryElement?: React.ReactElement;
};

const FTextArea = React.forwardRef<NativeTextInput, TextAreaProps>(
  (props: TextAreaProps, ref): React.ReactElement => {
    const {
      minHeight = 100,
      maxHeight = 300,
      accessoryElement,
      error,
      withMargin = true,
      containerStyle,
      ...rest
    } = props;
    const [height, setHeight] = React.useState(minHeight);
    const [isFocused, setIsFocused] = useState(false);
    const theme = useTheme();

    return (
      <View
        style={[
          styles.container,
          withMargin
            ? {
                marginBottom: 10,
              }
            : null,
          containerStyle,
        ]}
      >
        <FTextInput
          ref={ref}
          numberOfLines={5}
          {...rest}
          multiline
          height={100}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          contentStyle={[
            props.contentStyle,
            { minHeight: height, height: height, maxHeight: height },
          ]}
          withError={false}
          withMargin={false}
          style={[
            accessoryElement
              ? {
                  borderBottomRightRadius: 0,
                  borderBottomLeftRadius: 0,
                }
              : null,
          ]}
          onContentSizeChange={(e) => {
            if (Platform.OS !== 'web') {
              setHeight(Math.min(Math.max(e.nativeEvent.contentSize.height, minHeight), maxHeight));
            } else {
              const el = e?.target as unknown as HTMLElement;
              if (el) {
                el.style.height = '0';
                el.style.maxHeight = '0';
                el.style.minHeight = '0';
                let newHeight = el.offsetHeight - el.clientHeight + el.scrollHeight;
                newHeight = Math.min(Math.max(newHeight, minHeight), maxHeight);
                el.style.height = `${newHeight}px`;
                el.style.maxHeight = `${newHeight}px`;
                el.style.minHeight = `${newHeight}px`;
              }
            }
          }}
        />
        {accessoryElement ? (
          <View
            style={[
              styles.accessory,
              error?.message
                ? { borderColor: theme.colors.error }
                : isFocused
                ? { borderColor: theme.colors.primary }
                : null,
            ]}
          >
            {accessoryElement}
          </View>
        ) : null}
        {props.error?.message ? (
          <Text
            variant={'labelMedium'}
            style={{
              color: theme.colors.error,
              marginHorizontal: 18,
              bottom: 0,
              flex: 1,
              position: 'relative',
            }}
          >
            {props.error?.message}
          </Text>
        ) : null}
      </View>
    );
  }
);

FTextArea.displayName = 'TextInput';

export default FTextArea;

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
  },
  input: {
    height: 48,
    borderWidth: 1,
    borderColor: Color.colorGray_100,
    borderStyle: 'solid',
    borderRadius: Border.br_3xs,
    backgroundColor: Color.blanc,
  },
  accessory: {
    borderStyle: 'solid',
    borderColor: Color.colorGray_100,
    borderRadius: Border.br_3xs,
    minHeight: Border.br_3xs,
    borderWidth: 1,
    borderTopWidth: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
});
